import * as React from "react";
import { useState } from "react";
import { Survey, SurveyQuestion } from "@velaro/velaro-component-lib";
import { ExtSurveyQuestion } from "./QuestionEditor";

// ─── Template definitions ────────────────────────────────────────────────────

interface TemplateCard {
  id: string;
  emoji: string;
  title: string;
  subtitle: string;
  description: string;
  usedFor: string;
  dataLocation: string;
  buildQuestions: (metric?: string) => ExtSurveyQuestion[];
}

const PRECHAT_TEMPLATE: TemplateCard = {
  id: "prechat",
  emoji: "👋",
  title: "Pre-Chat Survey",
  subtitle: "Before the conversation starts",
  description:
    "Collect visitor information so your team knows who they're talking to before the first message arrives.",
  usedFor:
    "Routing chats to the right team, personalizing greetings, capturing leads.",
  dataLocation:
    "Stored as workflow variables on the engagement. Reference them with <<variable_name>> in any workflow node, rule condition, or bot message.",
  buildQuestions: () => [
    {
      type: "name",
      text: "What is your name?",
      required: true,
      variable: "name",
      options: []
    },
    {
      type: "email",
      text: "What is your email address?",
      required: true,
      variable: "email",
      options: []
    },
    {
      type: "choice",
      text: "What department can we help you with?",
      required: false,
      variable: "department",
      options: ["Sales", "Support", "Billing", "Other"]
    },
    {
      type: "textArea",
      text: "Briefly describe how we can help you today (optional):",
      required: false,
      variable: "pre_issue",
      options: []
    }
  ]
};

const POSTCHAT_TEMPLATE: TemplateCard = {
  id: "postchat",
  emoji: "💬",
  title: "Post-Chat Survey",
  subtitle: "After the conversation closes",
  description:
    "Measure how satisfied customers were after a support or sales interaction. You'll choose your satisfaction metric in the next step.",
  usedFor:
    "CSAT / NPS tracking, agent performance review, identifying service gaps.",
  dataLocation:
    "Stored as workflow variables and tracked as satisfaction metrics in the Reporting dashboard. Available in Zapier payloads under workflowVariables.",
  buildQuestions: (metric?: string) => {
    const questions: ExtSurveyQuestion[] = [];
    if (metric === "csat" || metric === "both") {
      questions.push({
        type: "star",
        text: "How would you rate your experience today? (1 = poor, 5 = excellent)",
        required: true,
        variable: "csat_rating",
        options: [],
        satisfactionMetricType: "csat"
      });
    }
    if (metric === "nps" || metric === "both") {
      questions.push({
        type: "netPromoterScore",
        text: "How likely are you to recommend us to a friend or colleague? (0 = not at all, 10 = extremely likely)",
        required: true,
        variable: "nps_score",
        options: [],
        satisfactionMetricType: "nps"
      });
    }
    questions.push({
      type: "choice",
      text: "Was your issue resolved today?",
      required: false,
      variable: "issue_resolved",
      options: ["Yes — fully resolved", "Partially resolved", "No — still open"]
    });
    if (metric === "nps" || metric === "both") {
      questions.push({
        type: "textArea",
        text: "What is the main reason for your score?",
        required: false,
        variable: "nps_reason",
        options: []
      });
    }
    questions.push({
      type: "textArea",
      text: "Any additional comments or suggestions?",
      required: false,
      variable: "feedback",
      options: []
    });
    return questions;
  }
};

const AGENT_TEMPLATE: TemplateCard = {
  id: "agent",
  emoji: "🧑‍💼",
  title: "Agent Rating Survey",
  subtitle: "Rate the agent who helped you",
  description:
    "Gather specific feedback about the agent — their knowledge, friendliness, and communication — separate from overall service satisfaction.",
  usedFor:
    "Agent coaching, performance reviews, identifying top performers and training needs.",
  dataLocation:
    "Stored as workflow variables on the engagement. Agent name is available as <<agentName>> in workflow context.",
  buildQuestions: () => [
    {
      type: "star",
      text: "How would you rate the agent who helped you today?",
      required: true,
      variable: "agent_rating",
      options: [],
      satisfactionMetricType: "star_rating"
    },
    {
      type: "choice",
      text: "How knowledgeable was the agent?",
      required: false,
      variable: "agent_knowledge",
      options: ["Very knowledgeable", "Somewhat knowledgeable", "Not knowledgeable"]
    },
    {
      type: "choice",
      text: "How friendly and professional was the agent?",
      required: false,
      variable: "agent_friendliness",
      options: ["Very friendly", "Professional but neutral", "Unfriendly"]
    },
    {
      type: "textArea",
      text: "Any comments about the agent? (optional)",
      required: false,
      variable: "agent_comments",
      options: []
    }
  ]
};

const NPS_STANDALONE: TemplateCard = {
  id: "nps_standalone",
  emoji: "📊",
  title: "NPS — Net Promoter Score",
  subtitle: "Standalone loyalty measurement",
  description:
    "The classic 0–10 recommendation question. Scores 9–10 = Promoters, 7–8 = Passives, 0–6 = Detractors. Your NPS = % Promoters − % Detractors.",
  usedFor:
    "Brand loyalty tracking, quarterly benchmarking, identifying detractors for follow-up.",
  dataLocation:
    "NPS score tracked in Reporting under Satisfaction Metrics. Reason text stored as a workflow variable. Both appear in Zapier payloads.",
  buildQuestions: () => [
    {
      type: "netPromoterScore",
      text: "How likely are you to recommend us to a friend or colleague? (0 = not at all, 10 = extremely likely)",
      required: true,
      variable: "nps_score",
      options: [],
      satisfactionMetricType: "nps"
    },
    {
      type: "textArea",
      text: "What is the main reason for your score?",
      required: false,
      variable: "nps_reason",
      options: []
    },
    {
      type: "choice",
      text: "What best describes your relationship with us?",
      required: false,
      variable: "nps_relationship",
      options: ["New customer", "Customer < 1 year", "Customer 1–3 years", "Customer 3+ years"]
    }
  ]
};

const TEMPLATES: TemplateCard[] = [
  PRECHAT_TEMPLATE,
  POSTCHAT_TEMPLATE,
  AGENT_TEMPLATE,
  NPS_STANDALONE
];

// ─── Metric picker (used for post-chat) ─────────────────────────────────────

interface MetricOption {
  id: string;
  label: string;
  tag: string;
  tagColor: string;
  pros: string[];
  cons: string[];
  bestFor: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  {
    id: "csat",
    label: "CSAT — Customer Satisfaction",
    tag: "⭐ Star rating (1–5)",
    tagColor: "bg-amber-100 text-amber-700",
    pros: [
      "Immediately intuitive — no explanation needed",
      "High response rates (visual, fast to complete)",
      "Easy to benchmark per agent or team"
    ],
    cons: [
      "Measures only the recent interaction, not long-term loyalty",
      "Scores tend to skew high (response bias)"
    ],
    bestFor: "Measuring support quality conversation-by-conversation."
  },
  {
    id: "nps",
    label: "NPS — Net Promoter Score",
    tag: "🔢 0–10 scale",
    tagColor: "bg-orange-100 text-orange-700",
    pros: [
      "Industry-standard loyalty metric — easy to benchmark externally",
      "Identifies promoters (9–10), passives (7–8), and detractors (0–6)",
      "Correlates with churn and growth"
    ],
    cons: [
      "Single score loses nuance about what drove it",
      "Requires follow-up question to understand reason"
    ],
    bestFor: "Quarterly loyalty tracking and identifying at-risk customers."
  },
  {
    id: "both",
    label: "CSAT + NPS — Combined",
    tag: "⭐ + 🔢",
    tagColor: "bg-violet-100 text-violet-700",
    pros: [
      "CSAT captures the immediate experience",
      "NPS captures loyalty intent",
      "More complete picture for executive reporting"
    ],
    cons: [
      "Longer survey — slightly lower completion rate",
      "Requires clear reporting to avoid confusion between metrics"
    ],
    bestFor:
      "Teams that report both per-interaction quality AND brand loyalty to leadership."
  }
];

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  onApply(survey: Survey): void;
  onClose(): void;
}

export default function SurveyTemplateWizard({ onApply, onClose }: Props) {
  const [step, setStep] = useState<"choose" | "metric" | "preview">("choose");
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateCard | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string>("csat");
  const [surveyName, setSurveyName] = useState("");

  function pickTemplate(t: TemplateCard) {
    setSelectedTemplate(t);
    const defaultName =
      t.id === "prechat"
        ? "Pre-Chat Survey"
        : t.id === "postchat"
        ? "Post-Chat CSAT Survey"
        : t.id === "agent"
        ? "Agent Rating Survey"
        : "NPS Survey";
    setSurveyName(defaultName);
    if (t.id === "postchat") {
      setStep("metric");
    } else {
      setStep("preview");
    }
  }

  function pickMetric(id: string) {
    setSelectedMetric(id);
    const label =
      id === "nps"
        ? "Post-Chat NPS Survey"
        : id === "both"
        ? "Post-Chat CSAT + NPS Survey"
        : "Post-Chat CSAT Survey";
    setSurveyName(label);
    setStep("preview");
  }

  function buildPreviewQuestions(): ExtSurveyQuestion[] {
    if (!selectedTemplate) return [];
    const metric =
      selectedTemplate.id === "postchat" ? selectedMetric : undefined;
    return selectedTemplate.buildQuestions(metric);
  }

  function apply() {
    const questions = buildPreviewQuestions();
    onApply({
      id: 0,
      name: surveyName || selectedTemplate?.title || "New Survey",
      questions: questions as SurveyQuestion[]
    });
  }

  const previewQuestions = step === "preview" ? buildPreviewQuestions() : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="font-semibold text-slate-800">Survey Template Wizard</div>
            <div className="text-xs text-slate-400 mt-0.5">
              {step === "choose" && "Step 1 of 3 — Choose a survey type"}
              {step === "metric" && "Step 2 of 3 — Choose your satisfaction metric"}
              {step === "preview" && "Step 3 of 3 — Review & confirm"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close wizard"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 1: choose template */}
          {step === "choose" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500 mb-4">
                Pick the type of survey you want to create. You can customise the questions after.
              </p>
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => pickTemplate(t)}
                  className="w-full text-left border border-slate-200 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl leading-none mt-0.5">{t.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 group-hover:text-blue-700">
                        {t.title}
                      </div>
                      <div className="text-xs text-slate-400 mb-1">{t.subtitle}</div>
                      <div className="text-sm text-slate-600">{t.description}</div>
                      <div className="mt-2 text-xs text-slate-400">
                        <span className="font-medium text-slate-500">Best for: </span>
                        {t.usedFor}
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-slate-300 group-hover:text-blue-400 shrink-0 mt-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: metric (post-chat only) */}
          {step === "metric" && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Choose how you want to measure satisfaction. Each metric has different strengths — we've explained the trade-offs below.
              </p>
              {METRIC_OPTIONS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => pickMetric(m.id)}
                  className="w-full text-left border border-slate-200 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-medium text-slate-800 group-hover:text-blue-700">
                          {m.label}
                        </span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.tagColor}`}>
                          {m.tag}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <div className="text-[10px] font-bold text-green-600 uppercase tracking-wide mb-1">Pros</div>
                          <ul className="space-y-1">
                            {m.pros.map((p, i) => (
                              <li key={i} className="text-xs text-slate-600 flex gap-1.5">
                                <span className="text-green-400 shrink-0">✓</span>
                                {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mb-1">Cons</div>
                          <ul className="space-y-1">
                            {m.cons.map((c, i) => (
                              <li key={i} className="text-xs text-slate-600 flex gap-1.5">
                                <span className="text-amber-400 shrink-0">△</span>
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-blue-600">
                        <span className="font-medium">Best for: </span>{m.bestFor}
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-slate-300 group-hover:text-blue-400 shrink-0 mt-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setStep("choose")}
                className="text-xs text-slate-400 hover:text-slate-600 mt-1"
              >
                ← Back
              </button>
            </div>
          )}

          {/* Step 3: preview */}
          {step === "preview" && selectedTemplate && (
            <div className="space-y-5">
              {/* Survey name */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Survey name</label>
                <input
                  type="text"
                  className="input w-full"
                  value={surveyName}
                  onChange={(e) => setSurveyName(e.target.value)}
                />
              </div>

              {/* Where data lives */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Where does this data go?
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {selectedTemplate.dataLocation}
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[
                    { label: "Reporting", icon: "📈", desc: "Satisfaction metrics dashboard" },
                    { label: "Workflows", icon: "⚡", desc: "Use <<variable>> in any node" },
                    { label: "Zapier", icon: "🔗", desc: "workflowVariables in payload" }
                  ].map((item) => (
                    <div key={item.label} className="bg-white border border-slate-200 rounded-lg p-2 text-center">
                      <div className="text-lg mb-1">{item.icon}</div>
                      <div className="text-xs font-medium text-slate-700">{item.label}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Question preview */}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Questions ({previewQuestions.length})
                </div>
                <div className="space-y-2">
                  {previewQuestions.map((q, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 border border-slate-200 rounded-lg p-3 bg-white"
                    >
                      <span className="text-xs font-bold text-slate-300 mt-0.5 w-5 text-center shrink-0">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-700">{q.text}</div>
                        {q.variable && (
                          <div className="mt-1 inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-600 font-mono text-[11px] px-2 py-0.5 rounded-full">
                            <span className="opacity-60">{"<<"}</span>
                            <span>{q.variable}</span>
                            <span className="opacity-60">{">>"}</span>
                          </div>
                        )}
                        {(q as ExtSurveyQuestion).satisfactionMetricType && (
                          <span className="ml-1 text-[11px] bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">
                            tracked as {(q as ExtSurveyQuestion).satisfactionMetricType?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="shrink-0">
                        {q.required ? (
                          <span className="text-[11px] text-red-500 font-medium">Required</span>
                        ) : (
                          <span className="text-[11px] text-slate-300">Optional</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  You can add, remove, or reorder questions after creating the survey.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setStep(selectedTemplate.id === "postchat" ? "metric" : "choose")}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                ← Back
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "preview" && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Creates a draft — you can edit before saving.
            </p>
            <button
              type="button"
              onClick={apply}
              className="inline-flex items-center gap-2 bg-[var(--color-primary)] hover:opacity-90 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Create survey from template
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
